import React from 'react';
import axios from 'axios';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Radio from '@mui/material/Radio';
import Typography from '@mui/material/Typography';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CallIcon from '@mui/icons-material/Call';

const countryCodes = [
  { country: 'Argentina', code: '+54' },
  { country: 'Australia', code: '+61' },
  { country: 'Austria', code: '+43' },
  { country: 'Bahrain', code: '+973' },
  { country: 'Bangladesh', code: '+880' },
  { country: 'Belgium', code: '+32' },
  { country: 'Bolivia', code: '+591' },
  { country: 'Brazil', code: '+55' },
  { country: 'Bulgaria', code: '+359' },
  { country: 'Canada', code: '+1' },
  { country: 'Chile', code: '+56' },
  { country: 'China', code: '+86' },
  { country: 'Colombia', code: '+57' },
  { country: 'Croatia', code: '+385' },
  { country: 'Cyprus', code: '+357' },
  { country: 'Czech Republic', code: '+420' },
  { country: 'Denmark', code: '+45' },
  { country: 'Ecuador', code: '+593' },
  { country: 'Estonia', code: '+372' },
  { country: 'Finland', code: '+358' },
  { country: 'France', code: '+33' },
  { country: 'Germany', code: '+49' },
  { country: 'Greece', code: '+30' },
  { country: 'Guyana', code: '+592' },
  { country: 'Hungary', code: '+36' },
  { country: 'Iceland', code: '+354' },
  { country: 'India', code: '+91' },
  { country: 'Iran', code: '+98' },
  { country: 'Iraq', code: '+964' },
  { country: 'Ireland', code: '+353' },
  { country: 'Israel', code: '+972' },
  { country: 'Italy', code: '+39' },
  { country: 'Japan', code: '+81' },
  { country: 'Jordan', code: '+962' },
  { country: 'Kuwait', code: '+965' },
  { country: 'Latvia', code: '+371' },
  { country: 'Lebanon', code: '+961' },
  { country: 'Lithuania', code: '+370' },
  { country: 'Luxembourg', code: '+352' },
  { country: 'Malaysia', code: '+60' },
  { country: 'Malta', code: '+356' },
  { country: 'Mexico', code: '+52' },
  { country: 'Nepal', code: '+977' },
  { country: 'Netherlands', code: '+31' },
  { country: 'Norway', code: '+47' },
  { country: 'Oman', code: '+968' },
  { country: 'Pakistan', code: '+92' },
  { country: 'Palestine', code: '+970' },
  { country: 'Paraguay', code: '+595' },
  { country: 'Peru', code: '+51' },
  { country: 'Poland', code: '+48' },
  { country: 'Portugal', code: '+351' },
  { country: 'Qatar', code: '+974' },
  { country: 'Romania', code: '+40' },
  { country: 'Russia', code: '+7' },
  { country: 'Saudi Arabia', code: '+966' },
  { country: 'Serbia', code: '+381' },
  { country: 'Singapore', code: '+65' },
  { country: 'Slovakia', code: '+421' },
  { country: 'Slovenia', code: '+386' },
  { country: 'South Africa', code: '+27' },
  { country: 'Spain', code: '+34' },
  { country: 'Sri Lanka', code: '+94' },
  { country: 'Suriname', code: '+597' },
  { country: 'Sweden', code: '+46' },
  { country: 'Switzerland', code: '+41' },
  { country: 'Syria', code: '+963' },
  { country: 'Turkey', code: '+90' },
  { country: 'Ukraine', code: '+380' },
  { country: 'United Arab Emirates', code: '+971' },
  { country: 'United Kingdom', code: '+44' },
  { country: 'United States', code: '+1' },
  { country: 'Uruguay', code: '+598' },
  { country: 'Venezuela', code: '+58' },
  { country: 'Yemen', code: '+967' },
]
  .map(({ country, code }) => ({
    country,
    code,
    label: `${country} (${code})`,
  }))
  .sort((a, b) => a.country.localeCompare(b.country));

const DEFAULT_COUNTRY = 'India';

const getEmptyForm = () => ({
  name: '',
  email: '',
  phone: '',
  country: DEFAULT_COUNTRY,
  requirement: '',
});

const RequestDemo = ({
  open: controlledOpen,
  onOpenChange,
  showButton = true,
  showDialog = true,
  onButtonClick,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? onOpenChange : setInternalOpen;
  const [form, setForm] = React.useState(getEmptyForm);
  const [errors, setErrors] = React.useState({});
  const [submitted, setSubmitted] = React.useState(false);
  const [apiError, setApiError] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setForm(getEmptyForm());
      setErrors({});
      setSubmitted(false);
      setApiError(false);
    }
  }, [open]);

  const handleClose = () => {
    setOpen(false);
    setForm(getEmptyForm());
    setErrors({});
    setSubmitted(false);
    setApiError(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Name is required';
    if (!form.email.trim()) {
      next.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      next.email = 'Enter a valid email address';
    }
    if (!form.country) next.country = 'Country code is required';
    if (!form.phone.trim()) {
      next.phone = 'Phone number is required';
    } else if (!/^[0-9\s-]{7,15}$/.test(form.phone.trim())) {
      next.phone = 'Enter a valid phone number';
    }
    if (!form.requirement) next.requirement = 'Please select a requirement';
    return next;
  };

  const handleSubmit = async () => {
    const next = validate();
    if (Object.keys(next).length) { setErrors(next); return; }

    const selectedCountry = countryCodes.find((c) => c.country === form.country);

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      country_code: selectedCountry?.code ?? '+91',
      requirement: form.requirement,
    };

    try {
      await axios.post('/api/user/enquire', payload);
      setSubmitted(true);
    } catch (_) {
      setApiError(true);
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      '&.Mui-focused fieldset': { borderColor: '#050f66' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#050f66' },
  };

  const contactBtnSx = {
    textTransform: 'none',
    borderRadius: '10px',
    fontWeight: 600,
    py: 1.2,
    fontSize: '0.95rem',
  };

  const handleOpen = () => {
    if (onButtonClick) {
      onButtonClick();
    } else {
      setOpen(true);
    }
  };

  return (
    <>
      {showButton && (
        <Button
          variant="contained"
          onClick={handleOpen}
          size="large"
          startIcon={<PlayCircleOutlineIcon />}
          sx={{
            background: '#050f66',
            color: 'white',
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 700,
            borderRadius: '12px',
            px: 4,
            py: 1.6,
            boxShadow: '0 8px 32px rgba(5, 15, 102, 0.35)',
            border: 'none',
            '&:hover': {
              background: '#0a1882',
              boxShadow: '0 12px 40px rgba(5, 15, 102, 0.45)',
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          Enquire now
        </Button>
      )}

      {showDialog && (
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? '16px 16px 0 0' : '20px',
            p: { xs: 0.5, sm: 1 },
            boxShadow: '0 24px 80px rgba(5, 15, 102, 0.2)',
            ...(isMobile && {
              position: 'fixed',
              bottom: 0,
              m: 0,
              width: '100%',
              maxHeight: '92vh',
            }),
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1.2rem', sm: '1.4rem' },
            color: '#050f66',
            pt: { xs: 2.5, sm: 3 },
            px: { xs: 2.5, sm: 3 },
          }}
        >
          {submitted ? 'Request Sent!' : apiError ? 'Something Went Wrong' : 'Enquire now'}
        </DialogTitle>

        <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, pt: 1 }}>
          {submitted ? (
            <Stack spacing={1} sx={{ py: 2 }}>
              <span style={{ fontSize: '1rem', color: '#444', lineHeight: 1.7 }}>
                Thanks, <strong>{form.name}</strong>! Our team will get back to you shortly.
              </span>
            </Stack>
          ) : apiError ? (
            <Stack spacing={3} sx={{ py: 2 }}>
              <Typography sx={{ color: '#444', fontSize: '0.95rem', lineHeight: 1.7 }}>
                We couldn't submit your request right now. Please reach out to us directly:
              </Typography>
              <Stack spacing={1.5}>
                <Button
                  disableElevation
                  fullWidth
                  variant="contained"
                  startIcon={<WhatsAppIcon />}
                  onClick={() => window.open('https://api.whatsapp.com/send/?phone=917904037932&text&app_absent=0', '_blank')}
                  sx={{ ...contactBtnSx, background: '#25d366', '&:hover': { background: '#25d366' } }}
                >
                  Chat on WhatsApp
                </Button>
                <Button
                  disableElevation
                  fullWidth
                  variant="outlined"
                  startIcon={<CallIcon />}
                  onClick={() => window.open('tel:7904037932', '_self')}
                  sx={{ ...contactBtnSx, color: '#050f66', borderColor: 'rgba(5,15,102,0.3)' }}
                >
                  Call Us
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={2.5} sx={{ pt: 1 }}>
              <TextField
                label="Name *"
                name="name"
                value={form.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
                fullWidth
                inputProps={{ style: { fontSize: 16 } }}
                sx={fieldSx}
              />
              <TextField
                label="Email *"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                fullWidth
                inputProps={{ inputMode: 'email', style: { fontSize: 16 } }}
                sx={fieldSx}
              />
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <FormControl error={!!errors.country} sx={{ ...fieldSx, minWidth: { sm: 220 }, flex: { sm: '0 0 220px' } }}>
                  <InputLabel id="country-code-label">Country Code *</InputLabel>
                  <Select
                    labelId="country-code-label"
                    name="country"
                    value={form.country || DEFAULT_COUNTRY}
                    label="Country Code *"
                    onChange={handleChange}
                    sx={{ borderRadius: '10px', fontSize: 16 }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 280 } } }}
                  >
                    {countryCodes.map((item) => (
                      <MenuItem key={item.country} value={item.country}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.country && <FormHelperText>{errors.country}</FormHelperText>}
                </FormControl>
                <TextField
                  label="Phone Number *"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  fullWidth
                  inputProps={{ inputMode: 'tel', style: { fontSize: 16 } }}
                  sx={{ ...fieldSx, flex: 1 }}
                />
              </Box>
              <FormControl error={!!errors.requirement}>
                <FormLabel
                  sx={{
                    color: '#444',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    '&.Mui-focused': { color: '#050f66' },
                  }}
                >
                  Requirement *
                </FormLabel>
                <RadioGroup
                  name="requirement"
                  value={form.requirement}
                  onChange={handleChange}
                  sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
                >
                  <FormControlLabel
                    value="rtmp"
                    control={<Radio sx={{ '&.Mui-checked': { color: '#050f66' } }} />}
                    label="RTMP Streaming"
                  />
                  <FormControlLabel
                    value="srt"
                    control={<Radio sx={{ '&.Mui-checked': { color: '#050f66' } }} />}
                    label="SRT Streaming"
                  />
                  <FormControlLabel
                    value="webapp"
                    control={<Radio sx={{ '&.Mui-checked': { color: '#050f66' } }} />}
                    label="Web App development"
                  />
                  <FormControlLabel
                    value="mobileapp"
                    control={<Radio sx={{ '&.Mui-checked': { color: '#050f66' } }} />}
                    label="Mobile App development"
                  />
                  <FormControlLabel
                    value="others"
                    control={<Radio sx={{ '&.Mui-checked': { color: '#050f66' } }} />}
                    label="Others"
                  />
                </RadioGroup>
                {errors.requirement && <FormHelperText>{errors.requirement}</FormHelperText>}
              </FormControl>
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2.5, sm: 3 },
            pb: { xs: 3, sm: 3 },
            pt: 1,
            gap: 1,
            flexDirection: { xs: 'column-reverse', sm: 'row' },
          }}
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            disableElevation
            fullWidth={isMobile}
            sx={{
              color: '#050f66',
              borderColor: 'rgba(5,15,102,0.3)',
              textTransform: 'none',
              borderRadius: '10px',
              px: 3,
              py: { xs: 1.3, sm: 1 },
              fontWeight: 600,
              '&:hover': { borderColor: '#050f66', background: 'rgba(5,15,102,0.04)' },
            }}
          >
            {submitted || apiError ? 'Close' : 'Cancel'}
          </Button>
          {!submitted && !apiError && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disableElevation
              fullWidth={isMobile}
              sx={{
                background: '#050f66',
                textTransform: 'none',
                borderRadius: '10px',
                px: 3.5,
                py: { xs: 1.3, sm: 1 },
                fontWeight: 600,
                '&:hover': { background: '#0a1882' },
              }}
            >
              Submit Request
            </Button>
          )}
        </DialogActions>
      </Dialog>
      )}
    </>
  );
};

export default RequestDemo;
